import { useState } from 'react';
import { X, FileText, Package, Grid, DollarSign, AlertTriangle, ShoppingCart } from 'lucide-react';
import ShareOptionsToolbar from './ShareOptionsToolbar';
import AddressedPersonModal from './AddressedPersonModal';
import ConvertToSaleModal from './ConvertToSaleModal';
import { updateQuotation } from '../../api/calculator';

const ViewQuotationModal = ({ quotation, onClose, onConversionComplete }) => {
  const [addressedModal, setAddressedModal] = useState({ open: false, actionType: '', callback: null });
  const [showConvertModal, setShowConvertModal] = useState(false);

  // Calculate totals from quotation
  const totals = quotation.calculations.reduce((acc, calc) => ({
    totalArea: acc.totalArea + (parseFloat(calc.total_area_sqm) || 0),
    totalCartons: acc.totalCartons + (parseInt(calc.total_cartons_needed) || parseInt(calc.cartons_needed) || 0),
    totalTiles: acc.totalTiles + (parseInt(calc.total_tiles_needed) || parseInt(calc.tiles_needed) || 0),
    totalCost: acc.totalCost + (parseFloat(calc.estimated_cost) || 0)
  }), { totalArea: 0, totalCartons: 0, totalTiles: 0, totalCost: 0 });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const generateQuotationText = (addressedTo) => {
    let text = `📋 QUOTATION SUMMARY for ${addressedTo}\n`;
    text += `━━━━━━━━━━━━━━━━━━━━\n`;
    text += `Date: ${new Date().toLocaleDateString('en-NG')}\n`;
    text += `Items: ${quotation.calculations.length}\n\n`;

    quotation.calculations.forEach((calc, index) => {
      text += `#${index + 1}: ${calc.title || calc.calculation_number || 'Calculation'}\n`;
      text += `Area: ${calc.total_area_sqm?.toFixed(2)} m²\n`;
      text += `Cartons: ${calc.total_cartons_needed || calc.cartons_needed}\n`;
      
      // Add product codes if available
      if (calc.results && calc.results.some(r => r.product_code)) {
        const productCodes = calc.results.filter(r => r.product_code).map(r => r.product_code).join(', ');
        text += `Products: ${productCodes}\n`;
      }
      
      if (calc.estimated_cost) {
        text += `Est. Cost: ${formatCurrency(calc.estimated_cost)}\n`;
      }
      text += `--------------------\n`;
    });

    text += `\n━━━━━━━━━━━━━━━━━━━━\n`;
    text += `TOTAL AREA: ${totals.totalArea.toFixed(2)} m²\n`;
    text += `TOTAL CARTONS: ${totals.totalCartons}\n`;
    text += `TOTAL ESTIMATE: ${formatCurrency(totals.totalCost)}\n`;
    text += `\n✨ Thank you for choosing us!\n`;
    
    return text;
  };

  const handleWhatsApp = (phone, addressedTo) => {
    const text = generateQuotationText(addressedTo);
    const encodedText = encodeURIComponent(text);
    window.open(`https://wa.me/${phone}?text=${encodedText}`, '_blank');
  };

  const handleEmail = (email, addressedTo) => {
    const text = generateQuotationText(addressedTo);
    const subject = encodeURIComponent(`Quotation Summary for ${addressedTo} - ${new Date().toLocaleDateString()}`);
    const body = encodeURIComponent(text);
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
  };

  const handleDownload = (addressedTo) => {
    const text = generateQuotationText(addressedTo);
    const blob = new Blob([text], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Quotation-${addressedTo.replace(/\s+/g, '_')}-${new Date().getTime()}.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handlePrint = (addressedTo) => {
    const printWindow = window.open('', '_blank');
    
    const calculationsHtml = quotation.calculations.map((calc, index) => {
      // Build product codes display
      const productCodes = calc.results?.filter(r => r.product_code).map(r => 
        `<span style="color: #D4AF37; font-family: monospace; font-weight: bold;">${r.product_code}</span>`
      ).join(' ') || '';
      
      // Build breakdown rows for multi-size
      const breakdownHtml = calc.results?.map((item, i) => `
        <div style="display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px dotted #eee;">
          <span>${item.size} ${item.product_code ? `<span style="color: #D4AF37; font-weight: bold;">(${item.product_code})</span>` : ''}</span>
          <span>${item.cartons_needed} cartons ${item.estimated_cost ? `• ${formatCurrency(item.estimated_cost)}` : ''}</span>
        </div>
      `).join('') || '';
      
      return `
        <tr>
          <td style="padding: 12px; border-bottom: 1px solid #eee;">#${index + 1}</td>
          <td style="padding: 12px; border-bottom: 1px solid #eee;">
            <div style="font-weight: bold; color: #1a1a2e;">${calc.title || calc.calculation_number || 'N/A'}</div>
            ${productCodes ? `<div style="margin-top: 4px;">${productCodes}</div>` : ''}
            ${breakdownHtml ? `<div style="margin-top: 8px; font-size: 0.85em;">${breakdownHtml}</div>` : ''}
          </td>
          <td style="padding: 12px; border-bottom: 1px solid #eee;">${calc.total_area_sqm?.toFixed(2) || '0'} m²</td>
          <td style="padding: 12px; border-bottom: 1px solid #eee;">${calc.total_cartons_needed || calc.cartons_needed || 0}</td>
          <td style="padding: 12px; border-bottom: 1px solid #eee;">${formatCurrency(calc.estimated_cost || 0)}</td>
        </tr>
      `;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Quotation for ${addressedTo}</title>
          <style>
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              padding: 40px;
              max-width: 900px;
              margin: 0 auto;
              color: #333;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #D4AF37;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .header h1 {
              color: #D4AF37;
              margin: 0;
            }
            .addressed-to {
              font-size: 1.3rem;
              margin-top: 15px;
              color: #1a1a2e;
            }
            .addressed-to span {
              color: #D4AF37;
              font-weight: bold;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            th {
              background: #f5f5f5;
              padding: 12px;
              text-align: left;
              border-bottom: 2px solid #D4AF37;
            }
            .totals {
              background: #f9f9f9;
              padding: 20px;
              border-radius: 8px;
              margin-top: 20px;
            }
            .totals-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 20px;
            }
            .total-item {
              text-align: center;
            }
            .total-value {
              font-size: 1.5em;
              font-weight: bold;
              color: #D4AF37;
            }
            .total-label {
              font-size: 0.9em;
              color: #666;
            }
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>QUOTATION SUMMARY</h1>
            <p class="addressed-to">for <span>${addressedTo}</span></p>
            <p>Generated on ${new Date().toLocaleString()}</p>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Calculation</th>
                <th>Area</th>
                <th>Cartons</th>
                <th>Est. Cost</th>
              </tr>
            </thead>
            <tbody>
              ${calculationsHtml}
            </tbody>
          </table>
          
          <div class="totals">
            <div class="totals-grid">
              <div class="total-item">
                <div class="total-value">${totals.totalArea.toFixed(1)} m²</div>
                <div class="total-label">Total Area</div>
              </div>
              <div class="total-item">
                <div class="total-value">${totals.totalCartons}</div>
                <div class="total-label">Total Cartons</div>
              </div>
              <div class="total-item">
                <div class="total-value">${totals.totalTiles}</div>
                <div class="total-label">Total Tiles</div>
              </div>
              <div class="total-item">
                <div class="total-value">${formatCurrency(totals.totalCost)}</div>
                <div class="total-label">Estimated Cost</div>
              </div>
            </div>
          </div>
          
          <div style="margin-top: 40px; text-align: center; color: #666; font-size: 0.9em;">
            <p>This is a computer-generated quotation. Valid for 7 days.</p>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  // Handle action with addressed person modal
  const handleBeforeAction = (actionType, callback) => {
    setAddressedModal({ open: true, actionType, callback });
  };

  const handleAddressedConfirm = async (addressedTo) => {
    // Save addressed person to DB
    if (quotation.id) {
        try {
            await updateQuotation(quotation.id, { addressed_person: addressedTo });
        } catch (error) {
            console.error('Failed to save addressed person:', error);
        }
    }

    setAddressedModal({ open: false, actionType: '', callback: null });
    if (addressedModal.callback) {
      addressedModal.callback(addressedTo);
    }
  };

  // Transform quotation to calculation format for ConvertToSaleModal
  const quotationAsCalculation = {
    calculation_number: quotation.quotation_number,
    total_area_sqm: totals.totalArea,
    results: quotation.calculations.flatMap(c => c.results || [])
  };

  const handleConversionComplete = () => {
    setShowConvertModal(false);
    if (onConversionComplete) onConversionComplete();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl shadow-xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-lightGray">
          <h2 className="flex items-center gap-2 text-xl font-semibold text-primary-black">
            <FileText className="w-6 h-6 text-accent-gold" />
            Quotation Details
          </h2>
          <div className="flex items-center gap-2">
            <ShareOptionsToolbar 
              onPrint={handlePrint}
              onWhatsApp={handleWhatsApp}
              onEmail={handleEmail}
              onDownload={handleDownload}
              onBeforeAction={handleBeforeAction}
            />
            <button
              className="w-9 h-9 flex items-center justify-center bg-secondary-offWhite rounded-lg text-primary-slate hover:bg-red-50 hover:text-red-500 transition-colors"
              onClick={onClose}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Calculations List */}
          {quotation.calculations.length > 0 ? (
            <div className="space-y-6">
              {quotation.calculations.map((calc, index) => {
                const isMultiSize = calc.results && calc.results.length > 0;
                const totalCartons = calc.total_cartons_needed || calc.cartons_needed;
                const totalTiles = calc.total_tiles_needed || calc.tiles_needed;

                return (
                  <div key={calc.id || index} className="bg-secondary-offWhite rounded-lg p-4 border border-secondary-lightGray">
                    {/* Calculation Header */}
                    <div className="flex items-center justify-between mb-2 pb-2 border-b border-secondary-lightGray">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-accent-gold text-white text-xs font-bold rounded">
                          #{index + 1}
                        </span>
                        <span className="font-medium text-primary-black">
                          {calc.title || calc.calculation_number || 'N/A'}
                        </span>
                      </div>
                      {calc.created_at && (
                        <span className="text-xs text-primary-slate">
                          {new Date(calc.created_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>

                    {/* Input Info (Faint) */}
                    <p className="text-xs text-gray-500 mb-3">
                      Input: {calc.area_length_m}m × {calc.area_width_m}m • Wastage: {calc.wastage_percent}%
                    </p>

                    {/* Multi-Size Results with Product Codes */}
                    {isMultiSize && (
                      <div className="mb-3">
                        <h4 className="text-sm font-semibold text-primary-black mb-2">Tile Breakdown</h4>
                        {calc.results.map((item, resIndex) => (
                          <div key={resIndex} className="p-2 bg-white rounded-lg mb-1 last:mb-0">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-primary-black">{item.size}</span>
                                {item.product_code && (
                                  <span className="font-mono text-accent-gold font-bold text-sm">{item.product_code}</span>
                                )}
                              </div>
                              <span className="text-xs text-primary-slate">{item.tile_dimensions}</span>
                            </div>
                            <div className="grid grid-cols-4 gap-2 text-xs">
                              <div className="text-center">
                                <p className="font-bold text-primary-black">{item.area_sqm}</p>
                                <p className="text-xs text-primary-slate">m²</p>
                              </div>
                              <div className="text-center">
                                <p className="font-bold text-primary-black">{item.tiles_needed}</p>
                                <p className="text-xs text-primary-slate">Tiles</p>
                              </div>
                              <div className="text-center">
                                <p className="font-bold text-accent-gold">{item.cartons_needed}</p>
                                <p className="text-xs text-primary-slate">Cartons</p>
                              </div>
                              {item.estimated_cost && (
                                <div className="text-center">
                                  <p className="font-bold text-accent-gold">{formatCurrency(item.estimated_cost)}</p>
                                  <p className="text-xs text-primary-slate">Est.</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Main Results */}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div className="p-3 bg-white border border-secondary-lightGray rounded-lg text-center">
                        <Grid className="w-5 h-5 text-primary-slate mx-auto mb-1" />
                        <p className="text-xl font-bold text-primary-black">{totalTiles}</p>
                        <p className="text-xs text-primary-slate">Tiles Needed</p>
                      </div>
                      <div className="p-3 bg-accent-gold/10 border border-accent-gold/30 rounded-lg text-center">
                        <Package className="w-5 h-5 text-accent-gold mx-auto mb-1" />
                        <p className="text-xl font-bold text-accent-gold">{totalCartons}</p>
                        <p className="text-xs text-primary-slate">Cartons to Buy</p>
                      </div>
                    </div>

                    {/* Cost Estimation (if available) */}
                    {calc.estimated_cost && (
                      <div className="p-3 bg-primary-black rounded-lg mb-3">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-2 font-semibold text-white text-sm">
                            <DollarSign className="w-4 h-4" /> Estimated Total:
                          </span>
                          <span className="text-xl font-bold text-accent-gold">{formatCurrency(calc.estimated_cost)}</span>
                        </div>
                      </div>
                    )}

                    {/* Stock Warning */}
                    {calc.sufficient_stock === false && (
                      <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">Insufficient Stock</p>
                          <p className="text-xs">Stock check failed. Please verify availability.</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-primary-slate">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No calculations in this quotation</p>
            </div>
          )}
        </div>

        {/* Totals Footer & Actions */}
        <div className="border-t border-secondary-lightGray bg-secondary-offWhite rounded-b-xl">
           {quotation.calculations.length > 0 && (
            <div className="px-6 py-4 grid grid-cols-4 gap-4 text-center border-b border-secondary-lightGray">
              <div>
                <p className="text-xl font-bold text-primary-black">{totals.totalArea.toFixed(1)} m²</p>
                <p className="text-xs text-primary-slate">Total Area</p>
              </div>
              <div>
                <p className="text-xl font-bold text-accent-gold">{totals.totalCartons}</p>
                <p className="text-xs text-primary-slate">Total Cartons</p>
              </div>
              <div>
                <p className="text-xl font-bold text-primary-black">{totals.totalTiles}</p>
                <p className="text-xs text-primary-slate">Total Tiles</p>
              </div>
              <div>
                <p className="text-xl font-bold text-green-600">{formatCurrency(totals.totalCost)}</p>
                <p className="text-xs text-primary-slate">Est. Total Cost</p>
              </div>
            </div>
           )}
           <div className="p-4 flex justify-end">
             <button 
               className="flex items-center justify-center gap-2 px-6 py-3 bg-accent-gold text-white rounded-lg font-semibold hover:bg-accent-goldDark transition-colors shadow-lg"
               onClick={() => setShowConvertModal(true)}
             >
               <ShoppingCart className="w-5 h-5" />
               Convert Quotation to Sale
             </button>
           </div>
        </div>
      </div>

      {/* Addressed Person Modal */}
      <AddressedPersonModal
        isOpen={addressedModal.open}
        onClose={() => setAddressedModal({ open: false, actionType: '', callback: null })}
        onConfirm={handleAddressedConfirm}
        actionType={addressedModal.actionType}
        documentType="quotation"
      />

      {/* Convert to Sale Modal */}
      {showConvertModal && (
        <ConvertToSaleModal
            calculation={quotationAsCalculation}
            onClose={() => setShowConvertModal(false)}
            onSuccess={handleConversionComplete}
        />
      )}
    </div>
  );
};

export default ViewQuotationModal;
